import { useMutation } from '@apollo/client';

import { datadogLogs } from '@datadog/browser-logs';
import type { LanguageCodes } from '@onevesthq/ov-enums';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { buildUpdateClientGroupPortfolioInput } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutationInput';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { branchKey } from '@utils/workflowBranchKey';
import { useSnackbar } from 'hephaestus/web';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  type HouseholdIpsFieldConfig,
  type HouseholdIpsFieldOptionKey,
  type HouseholdIpsValues,
  hasContent,
  IPS_NARRATIVE_FIELDS,
} from '../householdIpsComparison/householdIpsFields';
import { baseModelPortfolioId } from '../householdModelPortfolioDraft/entityBoundModel';
import { editedHoldings } from '../householdModelPortfolioDraft/householdAllocations';
import {
  DRAFT_ERROR_COPY,
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { useHouseholdModelPortfolioDraft } from '../householdModelPortfolioDraft/useHouseholdModelPortfolioDraft';
import {
  SETUP_ENTITY_BOUND_MODEL,
  UPDATE_CLIENT_GROUP_PORTFOLIO,
} from '../initializeHouseholdModelPortfolio/queries';
import { buildSetupEntityBoundModelInput } from '../initializeHouseholdModelPortfolio/setupEntityBoundModelAdapter';
import {
  type ReviewIpsSummaryOptions,
  ReviewIpsSummaryVisual,
} from './reviewIpsSummary.visual';

const TEST_ID = 'review-ips-summary';

/** Labels for the fields, for a recap the editing step never configured. */
const FALLBACK_LABEL_KEYS: Record<HouseholdIpsFieldOptionKey, string> = {
  ipsModelName: 'modelPortfolios:modelPortfolioTable.name',
  ipsDescription: 'modelPortfolios:portfolio.policyAndAssets.description',
  ipsInvestmentObjective:
    'modelPortfolios:portfolio.policyAndAssets.investmentObjective',
  ipsLiquidity: 'modelPortfolios:portfolio.policyAndAssets.liquidity',
  ipsDisclaimer: 'modelPortfolios:portfolio.policyAndAssets.disclaimer',
};

/**
 * What to show when the editing step left no configuration behind — a
 * completion resumed straight onto this step, or a branch that never passed
 * through it. Everything either model has something to say about is shown,
 * which is the closest guess available to what the advisor was given.
 */
const fieldConfigFromContent = (
  translateBackendString: (translatedObject: unknown) => string,
  models: (HouseholdIpsValues | undefined)[],
  translate: (key: string) => string,
  language: LanguageCodes,
): HouseholdIpsFieldConfig => {
  /**
   * A label the step wrote itself, filed where the step will look for it.
   *
   * A configured label arrives with a translation per language and is read back
   * by the language the advisor is working in. These are already in that
   * language — they came from the app's own translations — so filing them under
   * English would have the lookup miss them entirely and label nothing at all.
   */
  const label = (key: string) => ({ [language]: translate(key) });

  const config: HouseholdIpsFieldConfig = {
    enableAssetClassTargetAllocations: models.some(
      (model) => !!model?.targetAllocations?.length,
    ),
    assetClassTargetAllocationsLabel: label(
      'modelPortfolios:portfolio.policyAndAssets.assetAllocation',
    ),
  };

  for (const { optionKey, valueKey, richText } of IPS_NARRATIVE_FIELDS) {
    // Every model portfolio is named, so the name can never fail this and
    // would be shown — and left open to edit — on a workflow that never
    // offered it. Guessing only works for the fields a model can be silent on.
    const filled =
      optionKey !== 'ipsModelName' &&
      models.some((model) =>
        hasContent(translateBackendString(model?.[valueKey]), richText),
      );

    config[optionKey] = filled
      ? { enabled: true, label: label(FALLBACK_LABEL_KEYS[optionKey]) }
      : { enabled: false };
  }

  return config;
};

type SetupEntityBoundModelData = {
  setupEntityBoundModel?: {
    modelPortfolio?: { id?: string } | null;
    warnings?: string[] | null;
  } | null;
};

export type ReviewIpsSummaryProps = {
  options: ReviewIpsSummaryOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => void;
  stepLoading?: boolean;
};

/**
 * The IPS being proposed, beside the one the household has today, with the
 * proposed side still editable. Continue and cancel leave through their own
 * branches so the Change IPS flow can send a cancel somewhere other than the
 * rest of the edit.
 */
export const ReviewIpsSummary = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: ReviewIpsSummaryProps) => {
  const { translateBackendString, language } = useLocalization();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation([
    'workflowCompletions',
    'modelPortfolios',
    'shared',
  ]);
  const [saving, setSaving] = useState(false);
  const [setupEntityBoundModel, { loading: applying }] =
    useMutation<SetupEntityBoundModelData>(SETUP_ENTITY_BOUND_MODEL);
  const [updateClientGroupPortfolio, { loading: attaching }] = useMutation(
    UPDATE_CLIENT_GROUP_PORTFOLIO,
  );
  // The editing step normally leaves the draft in context, but a completion
  // resumed without one would otherwise recap nothing at all and still let the
  // advisor approve it.
  const {
    draft,
    baseline,
    loading: loadingDraft,
    error: draftError,
    retry,
  } = useHouseholdModelPortfolioDraft(clientGroupId);

  // Prefer the context-written household id, fall back to the completion
  // objectId threaded from `workflowCompletion.visual`.
  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  // The recap has no field settings of its own: it shows what the editing step
  // was configured with, so the two can never disagree about which fields this
  // household's IPS has.
  const fieldConfig = useMemo(
    () =>
      workflowData.householdIpsFieldConfig ??
      fieldConfigFromContent(
        translateBackendString,
        [draft, baseline],
        (key) => String(t(key)),
        language,
      ),
    [workflowData.householdIpsFieldConfig, draft, baseline, t, language],
  );

  // What the proposed bands are totalled against. The securities step leaves
  // its work in rows beside the holdings, and those are the newer word of the
  // two — the recap can sit either side of that step.
  const proposedHoldings = useMemo(() => editedHoldings(draft), [draft]);

  const cancel = () => {
    sendAnalytic(ovAnalyticsEvents.householdIpsSummaryCancel);
    // Without a cancel button the step has a single unnamed handle, so it must
    // not send a branch key the editor never drew.
    onNext(branchKey(1));
  };

  /**
   * Writes the approved IPS onto the household's own copy of the model, and
   * returns the copy it landed on.
   *
   * `setupEntityBoundModel` both creates the copy and applies the statement, so
   * a household still sharing a shelf model gets one of its own here rather
   * than having the shelf rewritten underneath every other household on it. A
   * household that already has a copy is named by the master it was taken from,
   * which is what lets product-service recognise and reuse it.
   */
  const applyToHouseholdModel = async (
    model: HouseholdModelPortfolioModel,
    values: HouseholdIpsValues,
    householdId: string,
    organizationId: string,
  ): Promise<string> => {
    const { data } = await setupEntityBoundModel({
      variables: {
        input: buildSetupEntityBoundModelInput({
          baseModelPortfolioId: baseModelPortfolioId(model),
          boundObjectId: householdId,
          organizationId,
          ips: values,
        }),
      },
    });

    const modelPortfolioId = data?.setupEntityBoundModel?.modelPortfolio?.id;
    if (!modelPortfolioId) {
      throw new Error('setupEntityBoundModel returned no model portfolio');
    }

    if ((data?.setupEntityBoundModel?.warnings ?? []).length > 0) {
      enqueueSnackbar(
        t('workflowCompletions:householdModelPortfolioIps.applyWarning'),
        { variant: 'warning' },
      );
    }

    // Only a household that was still on a shared model comes back holding a
    // different one; re-pointing the rest at what they already have would put
    // an attachment through account-service for a change that never happened.
    if (modelPortfolioId !== model.id) {
      await updateClientGroupPortfolio({
        variables: {
          input: buildUpdateClientGroupPortfolioInput({
            clientGroupId: householdId,
            financialProductId: modelPortfolioId,
          }),
        },
      });
    }

    return modelPortfolioId;
  };

  const approve = async (values: HouseholdIpsValues) => {
    if (!draft) {
      // Nothing to record, rather than a failed attempt to apply it — the
      // advisor has no retry to make here.
      enqueueSnackbar(
        t('workflowCompletions:householdModelPortfolioIps.applyMissingData'),
        { variant: 'error' },
      );
      return;
    }

    setSaving(true);
    try {
      // Always merge against the latest context rather than the render
      // closure, so a context write that landed while this step was open is
      // not clobbered. On the Edit branch the change is carried by the model
      // change proposal; applying here would put it live before anyone signs.
      await setWorkflowData((previousData) => ({
        ...previousData,
        modelPortfolioDetails: {
          ...previousData.modelPortfolioDetails,
          ...values,
        },
        currentHouseholdClientGroupId: householdClientGroupId,
      }));

      if (options?.applyChangesOnContinue) {
        const organizationId = workflowData.entityOrganizationId;
        if (!householdClientGroupId || !organizationId) {
          enqueueSnackbar(
            t(
              'workflowCompletions:householdModelPortfolioIps.applyMissingData',
            ),
            { variant: 'error' },
          );
          return;
        }

        try {
          const modelPortfolioId = await applyToHouseholdModel(
            draft,
            values,
            householdClientGroupId,
            organizationId,
          );

          await setWorkflowData((previousData) => ({
            ...previousData,
            entityBoundModelPortfolioId: modelPortfolioId,
            currentHouseholdClientGroupId: householdClientGroupId,
          }));
        } catch (caught) {
          datadogLogs.logger.error(
            'Household model portfolio IPS save failed',
            {
              clientGroupId: householdClientGroupId,
              modelPortfolioId: draft.id,
            },
            caught instanceof Error ? caught : undefined,
          );
          enqueueSnackbar(
            t('workflowCompletions:householdModelPortfolioIps.saveError'),
            { variant: 'error' },
          );
          return;
        }
      }

      sendAnalytic(ovAnalyticsEvents.householdIpsSummaryApprove);
      onNext(options?.enableCancel ? branchKey(0) : undefined);
    } finally {
      setSaving(false);
    }
  };

  if (loadingDraft || (!draft && !draftError)) {
    return (
      <HouseholdModelPortfolioDraftSkeleton
        title={translateBackendString(options?.title)}
        testId={TEST_ID}
      />
    );
  }

  if (draftError) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={translateBackendString(options?.title)}
        message={t(`workflowCompletions:${DRAFT_ERROR_COPY[draftError]}`)}
        testId={TEST_ID}
        onRetry={draftError === 'LOAD_FAILED' ? retry : undefined}
      />
    );
  }

  return (
    <ReviewIpsSummaryVisual
      options={options}
      fieldConfig={fieldConfig}
      proposed={{
        values: draft ?? {},
        actuals: {
          strategy: draft?.strategy,
          securities: proposedHoldings,
          assetClassBranches: draft?.assetClassBranches,
        },
      }}
      current={
        baseline && {
          values: baseline,
          actuals: {
            strategy: baseline.strategy,
            securities: baseline.children,
            assetClassBranches: baseline.assetClassBranches,
          },
        }
      }
      loading={stepLoading || saving || applying || attaching}
      onContinue={approve}
      onCancel={cancel}
    />
  );
};

export default ReviewIpsSummary;
